import { useContext, useEffect, useSyncExternalStore } from 'react';
import { PlaycastContext } from '../../Playcast';
import { store } from '../store';
import { faGamepadModern, faKeyboard, faMouse } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { captureStore } from '@playcastdotio/InputCapture';
import { isMobileDevice, isSafari } from '../../../api/utility';

export const Options = () => {
  const data = useContext(PlaycastContext);
  const access = useSyncExternalStore(captureStore.subscribeAccess, captureStore.getAccess);
  const startPlayerMode = async () => {
    let oldRequest = isSafari();
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

    if (!isSafari()) {
      try {
        if (isFirefox) {
          // on Firefox, we need to request fullscreen first
          // in order to request pointer lock and keyboard lock
          try {
            await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
          } catch (e) {
            console.log('Fullscreen not supported');
          }
        }

        const pointerLock = await navigator.permissions.query({ name: 'pointer-lock' as PermissionName });
        const keyboardLock = await navigator.permissions.query({ name: 'keyboard-lock' as PermissionName });

        if (pointerLock.state === 'granted' || pointerLock.state === 'prompt') {
          try {
            document.body.requestPointerLock({ unadjustedMovement: true });
          } catch (e) {
            console.log('Pointer lock not supported');
          }
        }

        if (keyboardLock.state === 'granted' || keyboardLock.state === 'prompt') {
          try {
            // try to lock the keyboard
            await navigator.keyboard.lock();
          } catch (e) {
            console.log('Keyboard lock not supported');
          }
        }
      } catch (e) {
        console.log('Pointer or keyboard lock permission API not supported, trying to request normally');
        oldRequest = true;
      }
    }
    if (oldRequest) {
      // Just request pointer lock and keyboard lock as normal
      try {
        document.body.requestPointerLock({ unadjustedMovement: true });
      } catch (e) {
        console.log('Pointer lock not supported');
      }
      try {
        // try to lock the keyboard
        await navigator.keyboard.lock();
      } catch (e) {
        console.log('Keyboard lock not supported');
      }
    }

    // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
    if (!window.navigator.standalone) {
      // on iOS, when in standalone, it's already fullscreen
      try {
        await document.body.requestFullscreen({ navigationUI: 'hide' });
      } catch (e) {
        console.log('Fullscreen not supported');
      }
    }

    store.updatePlayerMode('player');
  };

  const endPlayerMode = async () => {
    // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
    if (!document.fullscreenElement && !window.navigator.standalone) store.updatePlayerMode('spectator');
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', endPlayerMode, false);

    return () => {
      document.removeEventListener('fullscreenchange', endPlayerMode, false);
    };
  }, []);

  useEffect(() => {
    // Only show options if we have any type of input access
    if (access.gamepad || access.keyboard || access.mouse || access.touch) {
      store.updatePlayerOptionsOpen(true);
    }

    // Only exit fullscreen and update to spectator if we have NO input access at all
    const hasNoAccess = !access.gamepad && !access.keyboard && !access.mouse && !access.touch;
    if (hasNoAccess) {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      }
      store.updatePlayerMode('spectator');
    }
  }, [access.gamepad, access.keyboard, access.mouse, access.touch]);

  const handleMouseMove = () => {
    if (!document.pointerLockElement && document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    // Should not handle mouse move on mobile devices, I think
    // It is causing some weird behavior if we do
    if (isMobileDevice()) return;

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    data.mode.mode === 'spectator' &&
    data.options.open &&
    (access.gamepad || access.keyboard || access.mouse || access.touch ? (
      <div className='mx-auto flex w-full max-w-md flex-col items-center gap-6 rounded-md bg-slate-800 bg-opacity-95 p-8'>
        <div>
          <img src='/images/playcast_logo-96x96.png' alt='Logo' className='size-24' />
        </div>
        <div className='space-y-2'>
          <div className='text-center text-yellow-400'>Your controls:</div>
          <div className='flex flex-row gap-4 text-yellow-400'>
            <FontAwesomeIcon icon={faGamepadModern} size='xl' className={access.gamepad ? '' : 'opacity-30'} />
            <FontAwesomeIcon icon={faKeyboard} size='xl' className={access.keyboard ? '' : 'opacity-30'} />
            <FontAwesomeIcon icon={faMouse} size='xl' className={access.mouse ? '' : 'opacity-30'} />
          </div>
        </div>
        <button
          className='xs:block rounded-full border-2 border-gray-200 bg-amber-400 px-4 py-1.5 text-sm font-bold text-gray-900 shadow-sm hover:bg-amber-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600'
          onClick={startPlayerMode}
        >
          Play Now
        </button>
      </div>
    ) : (
      ''
    ))
  );
};
